import { useEffect, useState } from "react";

import Layout from "../../components/reusable/Layout";
import Title from "../../components/reusable/Title";
import RiskChartBar from "../../components/reusable/RiskChartBar";
import CardDescription from "../../components/reusable/CardDescription";
import SuggestionCard from "../../components/SuggestionCard";
import MasonCollinsSuggestionCard from "../../components/MasonCollinsSuggestionCard";
import Qrcode from "../../components/reusable/Qrcode";

import classes from "./RiskMetricPage.module.css";
import CustomSuggestionCard from "../../components/CustomSuggestionCard";
import { SvgConverterType } from "../../reports/Report";
import SuggestionCardWithImages from "../../components/SuggestionCardWithImages";
import SyncLifeCustomPage from "../SyncLifeCustomPage/SyncLifeCustomPage";
import { GNC_SUPPLEMENTS_DATA } from "./gncSupplementsData";
import { MASON_COLLINS_SUPPLEMENT_DATA } from "./masonCollinsSupplementData";

type Tab = { INDEX: number; NAME: string; VALUE: number };

type improveArea = { INDEX: number; TEXT: string; TITLE: string };

type Metric = {
  HOW_IT_IS_MEASURED: string;
  HOW_IT_IS_MEASURED_TEXT: string;
  INDEX: number;
  SUGGESTIONS: { IMPROVE_AREAS: improveArea[]; INDEX: number; TITLE: string }[];
  TITLE1: string;
  TITLE2: string;
  TOP: string;
  VALUE: number;
  WHAT_IT_IS: string;
  WHAT_IT_IS_TEXT: string;
  WHY_ITS_IMPORTANT: string;
  WHY_ITS_IMPORTANT_TEXT: string;
  RECCOMENDATIONS_TO_IMPROVE: string;

  SOURCES?: any;
};

interface Props {
  data: {
    DESCRIPTION: string;
    INDEX: number;
    METRICS: Metric[];
    NAVBAR: Tab[];
    RISK_METRICS: Metric[];
    PERFORMANCE_METRICS: Metric[];
    QRCODE: {
      FOOTER1: string;
      HEADER1: string;
      HEADER2: string;
      QRCODE: string;
    };
    ORGANS: any;
    TITLE: string;
    TITLE_PERFORMANCE_METRICS: string;
  };
  onSvgConverter: SvgConverterType;
  isRetoReport?: boolean;
  isNaturalSlimReport?: boolean;
  isSyncLifeReport?: boolean;
  isEvityReport?: boolean;
  isGNCReport?: boolean;
  isMasonCollinsReport?: boolean;
}

const RiskMetricPage = ({
  data,
  onSvgConverter,
  isRetoReport,
  isNaturalSlimReport,
  isSyncLifeReport,
  isEvityReport,
  isGNCReport,
  isMasonCollinsReport,
}: Props): JSX.Element => {
  const [imgRiskBar, setImgRiskBar] = useState(false);

  const riskMetric = data.ORGANS.CELLS.RISK_METRICS[0];
  useEffect(() => {
    onSvgConverter("metricPageChart", "imgMetricPageChart", 750);
    setImgRiskBar(true);
  }, [onSvgConverter]);

  const riskBar = (
    <div id="metricPageChart" className={classes.chart}>
      <RiskChartBar
        data={riskMetric}
        onSvgConverter={onSvgConverter}
        useLargerFont={true}
        customGap={true}
      />
    </div>
  );

  return (
    <>
      <Layout isEvityReport={isEvityReport}>
        <div id="imgMetricPageChart" className={classes.imgMetricPageChart}>
          {!imgRiskBar && riskBar}
        </div>

        <ul className={classes.inner}>
          <li className={classes.sub}>
            <CardDescription
              title={riskMetric.HOW_IT_IS_MEASURED}
              description={riskMetric.HOW_IT_IS_MEASURED_TEXT}
              useLargerFont={true}
              index={riskMetric.INDEX}
            />
          </li>
          <li className={classes.sub}>
            <CardDescription
              title={riskMetric.WHY_ITS_IMPORTANT}
              description={riskMetric.WHY_ITS_IMPORTANT_TEXT}
              useLargerFont={true}
              index={riskMetric.INDEX}
            />
          </li>
        </ul>
        <div className={classes.card_container}>
          <div className={classes.suggestion_tables_title}>
            {riskMetric.RECCOMENDATIONS_TO_IMPROVE}
          </div>
          <ul
            className={classes.suggestion_tables}
            style={{
              backgroundColor: isNaturalSlimReport ? "#5CB0A8" : "transparent",
            }}
          >
            {riskMetric.SUGGESTIONS.map((item: any, index: number) => {
              if (isNaturalSlimReport) {
                return <SuggestionCardWithImages key={index} data={item} />;
              }
              // For GNC: replace 3rd card (INDEX 3) with supplements, or hide if no data
              if (isGNCReport && item.INDEX === 3) {
                const supplementData =
                  GNC_SUPPLEMENTS_DATA[riskMetric.METRIC_TYPE];
                if (!supplementData) return null;
                return (
                  <SuggestionCard key={index} data={supplementData} />
                );
              }
              // For Mason Collins: replace the "Nutrition" row (INDEX 2)
              // with Performance Support supplements
              if (isMasonCollinsReport && item.INDEX === 2) {
                const supplementData =
                  MASON_COLLINS_SUPPLEMENT_DATA[riskMetric.METRIC_TYPE];
                if (supplementData) {
                  return (
                    <MasonCollinsSuggestionCard
                      key={index}
                      data={supplementData}
                    />
                  );
                }
              }
              return item.INDEX > 3 ? (
                <CustomSuggestionCard key={index} data={item} />
              ) : (
                <SuggestionCard key={index} data={item} />
              );
            })}
          </ul>
        </div>

        {!isRetoReport && (
          <Qrcode
            data={data.QRCODE}
            sources={riskMetric.SOURCES}
            isNaturalSlimReport={isNaturalSlimReport}
          />
        )}
      </Layout>
      {isSyncLifeReport && <SyncLifeCustomPage id={riskMetric.METRIC_TYPE} />}
    </>
  );
};

export default RiskMetricPage;
